import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CourseItem, CourseResource } from '../../shared/core/state/course/models';
import { Store } from '@ngrx/store';
import { State } from '../../shared/core/state';
import * as CourseActions from '../../shared/core/state/course/course.actions';
import { FormatterService } from '../../shared/util/formatter.service';
import { ResourceType } from '../../shared/core/state/resource/models';
import { OrderService } from '../../shared/core/state/order/order.service';
import { PendingCourseOrder } from '../../shared/core/state/order/order.model';

@Component({
  selector: 'black-course-details',
  templateUrl: './course-details.component.html',
  styleUrls: ['./course-details.component.css'],
  standalone: false,
  host: {
    ngSkipHydration: 'true',
  },
})
export class CourseDetailsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private store = inject<Store<State>>(Store);
  private orderService = inject(OrderService);
  public formatter = inject(FormatterService);

  activeModuleIndex = 0;
  pendingOrder?: PendingCourseOrder;

  get isOverdue(): boolean {
    return this.pendingOrder?.status === 'OVERDUE';
  }

  get isPendingApproval(): boolean {
    return this.pendingOrder?.status === 'AWAITING_APPROVAL';
  }

  get isPaymentPending(): boolean {
    return this.pendingOrder?.status === 'PENDING';
  }

  selectedCourse: CourseItem = {
    title: '',
    targetAudience: '',
    subject: '',
    settings: {
      maxStudents: 0,
      isPublic: false,
      price: 0,
      bannerImageResource: {
        path: '',
        _id: '',
        type: ResourceType.IMAGE,
        name: '',
        provider: '',
        access: '',
      },
    },
    details: {
      description: '',
      descriptionSummary: '',
      startDate: new Date(),
      medium: '',
      requirements: [],
      coverage: [],
      totalDurationHour: 0,
      totalDurationMinutes: 0,
    },
    contents: [],
    enrollments: [],
    _id: '',
  };

  get isEnrolled(): boolean {
    return Boolean(
      this.selectedCourse?.enrollments &&
        this.selectedCourse.enrollments.length > 0
    );
  }

  get bannerImageUrl(): string {
    return this.formatter.getResourceImagePath(
      this.selectedCourse?.settings?.bannerImageResource
    );
  }

  get categoryName(): string {
    return 'Course';
  }

  get totalLessons(): number {
    return (
      this.selectedCourse?.contents?.reduce(
        (total, module) => total + (module.resources ? module.resources.length : 0),
        0
      ) ?? 0
    );
  }

  get totalModules(): number {
    return this.selectedCourse?.contents?.length ?? 0;
  }

  get durationLabel(): string {
    const hours = this.selectedCourse?.details?.totalDurationHour || 0;
    const minutes = this.selectedCourse?.details?.totalDurationMinutes || 0;
    if (hours === 0 && minutes === 0) return 'Self-paced';
    if (hours === 0) return `${minutes} minutes`;
    if (minutes === 0) return `${hours} hours`;
    return `${hours} hours ${minutes} minutes`;
  }

  get price(): number {
    return this.selectedCourse?.settings?.price ?? 0;
  }

  get priceFormatted(): string {
    if (this.price === 0) return 'Free';
    return `LKR ${this.price.toLocaleString('en-LK')}`;
  }

  get mediumFormatted(): string {
    switch (this.selectedCourse?.details?.medium) {
      case 'TAMIL':
        return 'Tamil Medium';
      case 'SINHALA':
        return 'Sinhala Medium';
      default:
        return 'English Medium';
    }
  }

  ngOnInit(): void {
    const courseId = this.route.snapshot.paramMap.get('id');

    if (courseId) {
      this.store.dispatch(CourseActions.selectCourseItem({ id: courseId }));
      this.orderService
        .getPendingCourseOrders()
        .subscribe((map) => (this.pendingOrder = map.get(courseId)));
    } else {
      this.router.navigate(['/courses']);
    }

    this.store
      .select((state) => state.courses.selectedCourseItem)
      .subscribe((item) => {
        if (item) {
          this.selectedCourse = item;
        }
      });
  }

  activePreviewResource: CourseResource | null = null;
  previewVideoUrl = '';
  isPreviewModalOpen = false;

  get previewLessons(): CourseResource[] {
    return (
      this.selectedCourse?.contents
        ?.flatMap((c) => c.resources || [])
        .filter((r) => r.isFreePreview) || []
    );
  }

  get hasFreePreview(): boolean {
    return this.previewLessons.length > 0;
  }

  openPreview(resource: CourseResource): void {
    this.activePreviewResource = resource;
    this.isPreviewModalOpen = true;
    this.previewVideoUrl = resource.url || resource.resource || resource._id || '';
  }

  closePreview(): void {
    this.isPreviewModalOpen = false;
    this.activePreviewResource = null;
    this.previewVideoUrl = '';
  }

  enrollNow(): void {
    if (this.isOverdue || this.isPaymentPending) return this.goToOrderPayment();
    if (this.isEnrolled || this.price === 0) return this.goToLearning();
    if (this.isPendingApproval) return this.goToOrders();
    this.router.navigate(['/courses', this.selectedCourse._id, 'enroll']);
  }

  goToOrders(): void {
    this.router.navigate(['/dashboard/orders']);
  }

  goToOrderPayment(): void {
    if (this.pendingOrder?.orderId) {
      this.router.navigate(['/order/payment'], {
        queryParams: { orderId: this.pendingOrder.orderId },
      });
    } else {
      this.router.navigate(['/dashboard/orders']);
    }
  }

  goToLearning(): void {
    this.router.navigate(['/courses', this.selectedCourse._id, 'playlist']);
  }

  toggleModule(index: number): void {
    this.activeModuleIndex = this.activeModuleIndex === index ? -1 : index;
  }

  getResourceType(resource: CourseResource): 'video' | 'document' | 'link' {
    const t = (resource.type || '').toLowerCase();
    if (t === 'video') return 'video';
    if (t === 'document' || t === 'pdf' || /\.pdf($|[?#])/i.test(resource.url || '')) return 'document';
    if (t === 'link' || t === 'external' || (/^https?:\/\//i.test(resource.url || '') && !/youtu|vimeo|drive\.google|\.(mp4|webm|m3u8|mpd)($|[?#])/i.test(resource.url || ''))) return 'link';
    return 'video';
  }
}
